import { Injectable, inject } from '@angular/core';

import {
    ParticipantMarketStatus,
    ParticipantMarketType,
    ParticipantPriceStatus,
    ParticipantPriceType,
    ParticipantStatus,
    ParticipantType,
} from '@cds/betting-offer';
import { EventOption, EventOptionGroup, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { RacingConfig, Sitecore, TrapImages } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { ModalRef } from '@frontend/sports/modal/feature';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import {
    ForecastParticipantViewModel,
    GreyHoundsOptionTypes,
    GreyhoundParticipantDictionary,
    GreyhoundParticipantInfo,
    RacingOptionMarketTypes,
    RacingParticipantInfo,
    RacingUrlParam,
    RouteParams,
    WinGameFixture,
    WinGameFixturePreview,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';
import { HorseRacingRaceSummary } from '@frontend/sports/types/components/sitecore';
import { NativeAppService } from '@frontend/vanilla/core';
import { values } from 'lodash-es';
import { BehaviorSubject, Observable, map, shareReplay } from 'rxjs';

import { HorsesFixturePricingHelper } from '../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { EachWayMarketHelperService } from '../../option-group/each-way-market-helper.service';
import { TabBarItem } from '../../tab-bar/tab-bar.models';

@Injectable({
    providedIn: 'root',
})
export class RacingService {
    private _navigationTab$ = new BehaviorSubject<RouteParams>({ sportId: <SportConstant>0, activeTab: RacingUrlParam.Today });
    private modalRef: ModalRef;
    nativeAppService = inject(NativeAppService);

    private readonly trapImages$: Observable<TrapImages>;

    constructor(
        protected sitecore: Sitecore,
        private racingConfig: RacingConfig,
        private urlHelper: UrlHelperService,
        private eachWayMarketHelper: EachWayMarketHelperService,
        private trapImagesConfig: TrapImages,
        private horsesFixturePricingHelper: HorsesFixturePricingHelper,
    ) {
        this.trapImages$ = this.trapImagesConfig.whenReady.pipe(
            map(() => this.trapImagesConfig),
            shareReplay(1),
        );
    }

    get navigationTab$(): Observable<RouteParams> {
        return this._navigationTab$.asObservable();
    }

    getTraps$(regionId: number): Observable<StringDictionary<string>> {
        return this.getDogTraps(regionId);
    }

    selectTab(routeParams: RouteParams): void {
        this._navigationTab$.next(routeParams);
    }

    getNavigationTabs(routeParams: RouteParams): TabBarItem<string>[] {
        if (routeParams.sportId === SportConstant.Horses) {
            return this.getHorseNavTabs(routeParams.activeTab);
        }

        return this.getGreyhoundsNavTabs(routeParams.activeTab);
    }

    getRacingInfo(participant: WinGameParticipant): RacingParticipantInfo {
        return {
            jockey: participant.properties!.jockey || participant.racingPostInfo?.horseData.jockeyName,
            trainer:
                participant.properties!.trainer ||
                participant.racingPostInfo?.horseData.trainerName ||
                participant.racingPostInfo?.greyhoundsData.trainerName,
            isFavorite: participant.properties!.type === ParticipantType.Favourite,
        };
    }

    getTrapInfo(participant: WinGameParticipant): RacingParticipantInfo {
        return {
            trainer: participant.properties!.trainer || participant.racingPostInfo?.greyhoundsData.trainerName,
            isFavorite: participant.properties!.type === ParticipantType.Favourite,
        };
    }

    filterRaceSummary<T extends WinGameFixturePreview>(fixture: T): T {
        return {
            ...fixture,
            addons: {
                ...fixture.addons,
                raceSummary: this.getRaceSummary(fixture.addons.raceSummary, fixture.addons.isResulted || fixture.addons.isRaceOver),
            },
        };
    }

    setModalRef(modalRef: ModalRef): void {
        this.modalRef = modalRef;
    }

    getModalRef(): ModalRef {
        return this.modalRef;
    }

    getMarketName(fixture: WinGameFixture, skipCalculation = false): string {
        const market = fixture.isOptionBet
            ? this.eachWayMarketHelper.getOptionMarketWinOptions(fixture.optionMarkets)[0]
            : this.eachWayMarketHelper.findWinOptions(fixture.participants)[0] || null;

        if (!market) return '';

        const { EachWayAbbr } = this.sitecore.betslip.messages;
        const winMarketName = this.sitecore.horseRacing.markets.WinShort;

        if (skipCalculation) {
            return market.isEachWay ? `${winMarketName} / ${EachWayAbbr}` : winMarketName;
        }

        if (market.isEachWay && market.placeTerms) {
            const eachWayTerms = this.eachWayMarketHelper.calculateEachWayTerms(market.placeTerms);
            const formattedTerms =
                !this.nativeAppService.isTerminal && fixture.sport.id !== SportConstant.Greyhounds ? `(${eachWayTerms})` : eachWayTerms;

            return `${winMarketName} & ${this.sitecore.betslip.messages.EachWayAbbr} ${formattedTerms}`;
        }
        return winMarketName;
    }

    getPlaceTermValue(participants: WinGameParticipant[]): number | null {
        const placeTerm = participants
            .flatMap((p) => p.options)
            .filter((o) => o.marketType === ParticipantMarketType.Win && o.priceType === ParticipantPriceType.Fixed)
            .map((m) => ({
                isEachWay: m.properties.isEachWay || false,
                placeTerms: m.properties.placeTerms,
            }))[0];

        if (!placeTerm?.isEachWay) {
            return null;
        }

        return placeTerm.placeTerms.places;
    }

    private getHorseNavTabs(activeTab: RacingUrlParam): TabBarItem<string>[] {
        const tabs = [
            {
                icon: 'sports-racing-calender',
                title: this.sitecore.horseRacing.navigation.Today,
                url: this.urlHelper.getRaceUrl(SportConstant.Horses, RacingUrlParam.Today),
                active: RacingUrlParam.Today === activeTab,
                id: RacingUrlParam.Today,
                ignoreBrowserHistory: true,
            },
            {
                icon: 'sports-racing-calender',
                title: this.sitecore.horseRacing.navigation.Tomorrow,
                url: this.urlHelper.getRaceUrl(SportConstant.Horses, RacingUrlParam.Tomorrow),
                active: RacingUrlParam.Tomorrow === activeTab,
                id: RacingUrlParam.Tomorrow,
                ignoreBrowserHistory: true,
            },
            {
                icon: 'sports-racing-specials',
                title: this.sitecore.horseRacing.navigation.AntepostAndSpecials,
                url: this.urlHelper.getRaceUrl(SportConstant.Horses, RacingUrlParam.Future),
                active: RacingUrlParam.Future === activeTab,
                id: RacingUrlParam.Future,
                ignoreBrowserHistory: true,
            },
            {
                icon: 'sports-racing-result',
                title: this.sitecore.horseRacing.navigation.TodaysResults,
                url: this.urlHelper.getRaceUrl(SportConstant.Horses, RacingUrlParam.TodaysResults),
                active: RacingUrlParam.TodaysResults === activeTab,
                id: RacingUrlParam.TodaysResults,
                ignoreBrowserHistory: true,
            },
        ];
        const currentSportTabs = this.racingConfig.enabledTabs[SportConstant.Horses];

        return currentSportTabs?.length > 0 ? tabs.filter((tab) => currentSportTabs.some((t) => t === tab.id)) : tabs;
    }

    private getGreyhoundsNavTabs(activeTab: RacingUrlParam): TabBarItem<string>[] {
        const tabs = [
            {
                icon: 'sports-racing-calender',
                title: this.sitecore.horseRacing.navigation.Today,
                url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, RacingUrlParam.Today),
                active: RacingUrlParam.Today === activeTab,
                id: RacingUrlParam.Today,
                ignoreBrowserHistory: true,
            },
            {
                icon: 'sports-racing-calender',
                title: this.sitecore.horseRacing.navigation.Tomorrow,
                url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, RacingUrlParam.Tomorrow),
                active: RacingUrlParam.Tomorrow === activeTab,
                id: RacingUrlParam.Tomorrow,
                ignoreBrowserHistory: true,
            },
            {
                icon: 'sports-racing-specials',
                title: this.sitecore.horseRacing.navigation.AntepostAndSpecials,
                url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, RacingUrlParam.Future),
                active: RacingUrlParam.Future === activeTab,
                id: RacingUrlParam.Future,
                ignoreBrowserHistory: true,
            },
            {
                icon: 'sports-racing-result',
                title: this.sitecore.horseRacing.navigation.TodaysResults,
                url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, RacingUrlParam.TodaysResults),
                active: RacingUrlParam.TodaysResults === activeTab,
                id: RacingUrlParam.TodaysResults,
                ignoreBrowserHistory: true,
            },
        ];
        const currentSportTabs = this.racingConfig.enabledTabs[SportConstant.Greyhounds];

        return currentSportTabs?.length > 0 ? tabs.filter((tab) => currentSportTabs.some((t) => t === tab.id)) : tabs;
    }

    private getRaceSummary(key: string, resulted?: boolean): string {
        const hasExcludedRaceSummary = values(this.sitecore.horseRacing.excludedRaceSummary).some((r) => r === key);

        return hasExcludedRaceSummary || resulted
            ? ''
            : this.sitecore.horseRacing.raceSummary[key as keyof HorseRacingRaceSummary]?.toLowerCase() || key?.toLowerCase();
    }

    isOptionVisible(participant: WinGameParticipant, marketType: ParticipantMarketType, preferredPriceType: ParticipantPriceType): boolean {
        const options = this.horsesFixturePricingHelper.getPriceForMarketType(participant, marketType);
        const preferredOption = options.find((o) => o.priceType === preferredPriceType);

        return !!(preferredOption?.participantPriceStatus === ParticipantPriceStatus.Visible);
    }

    isLetter(form: string) {
        return form !== undefined && form.length === 1 && form.match(/[a-z]/i);
    }

    isMarketSuspended(model: ForecastParticipantViewModel[], marketType: ParticipantMarketType, isOptionBet: boolean): boolean {
        if (isOptionBet) {
            return model.some((o) => o.marketStatus);
        }
        return model
            .flatMap((p) => p.options)
            .filter((o) => o.marketType === marketType)
            .some((o) => o.marketStatus === ParticipantMarketStatus.Suspended);
    }

    getAvailableTrapImages(option: EventOption, event: WinGameFixture): number[] | undefined {
        if (option.optionTypes?.length) {
            const trapParticipants = event.participants.filter((p) => p.properties?.type === ParticipantType.Trap);
            const participantCount = trapParticipants.length;
            const availableTrapImages = trapParticipants
                .filter((p) => p.status !== ParticipantStatus.NonRunner)
                .flatMap((p) => p.participantId)
                .sort();
            switch (option.optionTypes[0]) {
                case GreyHoundsOptionTypes.Inside:
                    return availableTrapImages.filter((trapNumber) => trapNumber <= participantCount / 2);
                case GreyHoundsOptionTypes.Outside:
                    return availableTrapImages.filter((trapNumber) => trapNumber > participantCount / 2);
                case GreyHoundsOptionTypes.Odds:
                    return availableTrapImages.filter((trapNumber) => trapNumber % 2 !== 0);
                case GreyHoundsOptionTypes.Evens:
                    return availableTrapImages.filter((trapNumber) => trapNumber % 2 === 0);
                default:
                    return undefined;
            }
        }

        return undefined;
    }

    private getGreyhoundInfo(participant: WinGameParticipant, associatedFixtureParticipant: WinGameParticipant): GreyhoundParticipantInfo {
        if (participant.properties?.type === ParticipantType.Trap) {
            return {
                trapNumber: participant.participantId,
                greyhoundName: associatedFixtureParticipant.name.value,
            };
        } else {
            return {
                trapNumber: associatedFixtureParticipant.participantId,
                greyhoundName: participant.name.value,
            };
        }
    }

    getGrehoundTrapDogMapping(participants: WinGameParticipant[]): GreyhoundParticipantDictionary {
        const greyhoundParticipantTrapDogMapping: { [id: number]: GreyhoundParticipantInfo } = {};
        participants.forEach((p) => {
            const associatedParticipant = participants.find(
                (gp) => gp.id === p.properties?.associatedFixtureParticipantId && p.properties?.type != ParticipantType.Favourite,
            );
            if (associatedParticipant) {
                greyhoundParticipantTrapDogMapping[p.participantId] = this.getGreyhoundInfo(p, associatedParticipant);
            }
        });

        return greyhoundParticipantTrapDogMapping;
    }

    getMarketOptionGroup(optionGroups: EventOptionGroup[], racingOptionMarketTypes: RacingOptionMarketTypes): EventOptionGroup | undefined {
        return optionGroups.find((o) => o.parameters?.some((p) => p.key === Tv2MarketParameters.MarketType && p.value === racingOptionMarketTypes));
    }

    getWinOptionGroup(optionGroups: EventOptionGroup[]): EventOptionGroup {
        return this.getMarketOptionGroup(optionGroups, RacingOptionMarketTypes.WinEw)!;
    }

    private getDogTraps(regionId: number): Observable<StringDictionary<string>> {
        return this.trapImages$.pipe(
            map((trapImage) => {
                const regionDogTraps = trapImage.regionDogTraps?.find((regionDogTrap) => regionDogTrap.regionIds.includes(regionId));
                const dogTraps = regionDogTraps?.trapImages ?? trapImage.dogTrap;

                return dogTraps.reduce((res, currentDogTrap) => {
                    res[currentDogTrap.title] = currentDogTrap.imageUrl!;

                    return res;
                }, {} as StringDictionary<string>);
            }),
        );
    }
}
